import { useState, useEffect } from 'react';
import { ShoppingCartIcon } from '@heroicons/react/24/solid';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams, Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { RootState } from '../../../redux/store';

interface CartItemAnimationProps {
  startPosition: { x: number; y: number };
  onComplete: () => void;
}

function CartItemAnimation({ startPosition, onComplete }: CartItemAnimationProps) {
  return (
    <motion.div
      className='fixed z-50 h-10 w-10 rounded-full bg-primary shadow-lg'
      initial={{
        x: startPosition.x,
        y: startPosition.y,
        scale: 1,
        opacity: 0.9,
      }}
      animate={{
        x: 'calc(100vw - 70px)',
        y: 'calc(100vh - 70px)',
        scale: 0.3,
        opacity: 0,
      }}
      transition={{
        type: 'spring',
        duration: 0.8,
        bounce: 0.4,
        damping: 8,
      }}
      onAnimationComplete={onComplete}
    />
  );
}

function FloatingCartButton() {
  const { t } = useTranslation();
  const { locale } = useParams();
  const itemCount = useSelector((state: RootState) => state.cart.itemCount);
  const [showButton, setShowButton] = useState(false);
  const [animations, setAnimations] = useState<{id: string, position: {x: number, y: number}}[]>([]);
  const [pulsing, setPulsing] = useState(false);
  const location = useLocation();

  const isCheckoutRoute = location.pathname.includes(`/${locale}/checkout`);

  const addAnimation = (position: {x: number, y: number}) => {
    const id = Math.random().toString(36).substring(2, 9);
    setAnimations((prev) => [...prev, { id, position }]);
  };

  const removeAnimation = (id: string) => {
    setAnimations((prev) => prev.filter((anim) => anim.id !== id));
  };

  useEffect(() => {
    if (itemCount > 0 && !isCheckoutRoute) {
      setShowButton(true);
    } else {
      const timer = setTimeout(() => {
        setShowButton(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [itemCount, isCheckoutRoute]);

  useEffect(() => {
    const handleAddToCartAnimation = (e: CustomEvent) => {
      addAnimation(e.detail.position);
      setShowButton(true);

      setTimeout(() => {
        setPulsing(true);
        setTimeout(() => setPulsing(false), 500);
      }, 700);
    };

    window.addEventListener('addToCartAnimation' as any, handleAddToCartAnimation as EventListener);

    return () => {
      window.removeEventListener('addToCartAnimation' as any, handleAddToCartAnimation as EventListener);
    };
  }, []);

  const openCartModal = () => {
    window.dispatchEvent(new CustomEvent('openCartModal'));
  };

  return (
    <>
      {animations.map((animation) => (
        <CartItemAnimation
          key={animation.id}
          startPosition={animation.position}
          onComplete={() => removeAnimation(animation.id)}
        />
      ))}
      <AnimatePresence>
        {showButton && (
          <motion.div
            initial={{ opacity: 0, scale: 0.5, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.5, y: 20 }}
            className='fixed bottom-5 left-5 rtl:right-5 z-40 items-center gap-3 hidden lg:flex'
          >
            <button
              type='button'
              onClick={openCartModal}
              className={`flex h-14 w-14 items-center justify-center rounded-full bg-primary text-white shadow-lg hover:bg-primary-dark ${pulsing ? 'ring-4 ring-primary ring-opacity-50' : ''}`}
              aria-label={t('cart.viewCart')}
            >
              <motion.div
                animate={pulsing ? {
                  scale: [1, 1.2, 1],
                } : {}}
                transition={{ duration: 0.5 }}
              >
                <ShoppingCartIcon className='h-6 w-6' />
              </motion.div>
              {itemCount > 0 && (
                <motion.span
                  className='absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full bg-secondary text-xs font-bold text-white'
                  animate={pulsing ? {
                    scale: [1, 1.3, 1],
                  } : {}}
                  transition={{ duration: 0.5 }}
                >
                  {itemCount}
                </motion.span>
              )}
            </button>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ delay: 0.1 }}
            >
              <Link
                to={`/${locale}/checkout`}
                className='flex h-12 items-center justify-center rounded-full bg-secondary px-6 text-white shadow-lg hover:bg-secondary-dark rtl:flex-row-reverse'
              >
                <span className='font-medium text-sm whitespace-nowrap'>{t('shared.continueToCheckout')}</span>
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default FloatingCartButton;
